import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import _pick from 'lodash/pick';
import { useLocalStorage } from 'usehooks-ts';
import {
  BackgroundJob,
  DataImport,
  DataImportCreateInputObject,
  DataImportStatusEnum,
  DataImportUpdateInputObject,
  FilterGroupingEnum,
  useDataImportConfigQuery,
  useDataImportCreateMutation,
  useDataImportDestroyMutation,
  useDataImportsLazyQuery,
  useDataImportUpdateMutation,
  useBackgroundJobCancelMutation,
} from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import toast from '@lib/components/Toast/Toast';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { Modals } from '@lib/enums/modals';
import useSearchParam from '@lib/hooks/useSearchParam';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import { UseModalManagementResult } from '@lib/hooks/useModalManagement';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';
import FieldNames from '@lib/enums/fieldNames/importFormFieldNames';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import useDataImportsQueryHook from 'utils/fetch/hooks/useDataImportsQueryHook';
import useDataImportQueryHook from 'utils/fetch/hooks/useDataImportQueryHook';
import { IMPORT_FILTER_FORM } from '@lib/enums/localStorageKeys';
import { EventBusEvents, jobEventBus } from '@lib/utils/eventBus';
import { POLL_INTERVAL } from 'components/JobTracker/constants';
import isDataImportFinished from './isDataImportFinished';
import { IMPORT_FILTER_EMPTY_VALUES } from '../constants';
import transformConfig from './normalizeConfig';

interface Props {
  formRef: FormRefType;
  useBulkSelectResult: UseBulkSelectResult<DataImport>;
  useModalManagementResult: UseModalManagementResult;
}

export default function useActions(props: Props) {
  const { formRef, useModalManagementResult, useBulkSelectResult } = props;
  const { openModal, closeModal } = useModalManagementResult;

  const { t } = useTranslation();
  const [importFormLoading, setImportFormLoading] = useState(false);
  const [activeImportId, setActiveImportId] = useState<string | undefined>();
  const [activeImportIds, setActiveImportIds] = useState<string[]>([]);
  const [searchQuery] = useSearchParam();
  const [paginationParams] = usePaginationParams();
  const [filterForm] = useLocalStorage(IMPORT_FILTER_FORM, {});
  const filterFormValues = _get(filterForm, 'values', {});

  // Queries
  const { data } = useDataImportConfigQuery({
    fetchPolicy: 'cache-and-network',
  });
  const config = transformConfig(data?.data);
  const dataImportsQuery = useDataImportsQueryHook({
    filterParams: filterFormValues,
    orderingParams: paginationParams,
    paginationParams,
    searchQuery,
  });
  const { refetch: refetchDataImports } = dataImportsQuery;
  const [
    fetchActiveImports,
    {
      data: activeImportsData,
      startPolling: activeImportsStartPolling,
      stopPolling: activeImportsStopPolling,
    },
  ] = useDataImportsLazyQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    pollInterval: POLL_INTERVAL,
  });

  useEffect(() => {
    if (!activeImportIds.length) return;
    const collection = activeImportsData?.data?.collection ?? [];
    // Data may still belong to the previous variables — only treat the
    // tracked imports as finished once every one of them is in the response.
    const allActiveImportsAreFinished = activeImportIds.every((id) => {
      const item = collection.find((dataImport) => dataImport.id === id);
      return !!item && isDataImportFinished(item.status);
    });
    if (allActiveImportsAreFinished) {
      activeImportsStopPolling();
      setActiveImportIds([]);
      refetchDataImports();
    }
  }, [
    activeImportIds,
    activeImportsData,
    activeImportsStopPolling,
    refetchDataImports,
  ]);
  const dataImportQuery = useDataImportQueryHook({
    id: activeImportId,
  });
  const { data: activeImportData } = dataImportQuery;
  const activeImport =
    activeImportId && activeImportId === activeImportData?.id
      ? activeImportData
      : undefined;

  // Mutations
  const [dataImportCreateMutation, dataImportCreateMutationResult] =
    useDataImportCreateMutation();
  const [dataImportUpdateMutation, dataImportUpdateMutationResult] =
    useDataImportUpdateMutation();
  const [dataImportDestroyMutation, dataImportDestroyMutationResult] =
    useDataImportDestroyMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });
  const [cancelJobMutation, cancelJobMutationResult] =
    useBackgroundJobCancelMutation();

  // Actions
  const onSubmitDataImportForm = useCallback(
    (values: Values) => {
      setImportFormLoading(true);
      const kind = _get(values, [FieldNames.Type, 'value']);
      const attachmentAttached = _get(
        values,
        `${FieldNames.FileUpload}._destroy`,
      )
        ? undefined
        : {
            file: {
              id: _get(values, `${FieldNames.FileUpload}.file.id`),
              storage: _get(values, `${FieldNames.FileUpload}.file.storage`),
              metadata: _get(values, `${FieldNames.FileUpload}.file.metadata`),
            },
            filename: _get(
              values,
              `${FieldNames.FileUpload}.file.metadata.filename`,
            ),
          };
      const ignoreRowsWithErrors = _get(values, FieldNames.IgnoreErrors, false);
      if (activeImportId) {
        const inputObject = {
          attachmentAttached,
          ignoreRowsWithErrors,
        } as DataImportUpdateInputObject;
        dataImportUpdateMutation({
          variables: { attributes: inputObject, id: activeImportId },
          onCompleted: (res) => {
            const id = _get(res, 'data.id');
            const backgroundJob = _get(res, 'data.backgroundJob') as
              BackgroundJob | undefined;
            toast({
              content: t('create-data-import-success'),
            });
            dataImportsQuery.refetch();
            if (backgroundJob) {
              jobEventBus.emit(EventBusEvents.JobCreated, backgroundJob);
            }
            if (id) setActiveImportId(id);
            if (id) setActiveImportIds((prev) => [...prev, id]);
          },
          onError: (error) => {
            setImportFormLoading(false);
            showGraphQLErrors(error, t);
          },
        });
      } else {
        const inputObject = {
          kind,
          attachmentAttached,
          ignoreRowsWithErrors,
        } as DataImportCreateInputObject;
        dataImportCreateMutation({
          variables: { attributes: inputObject },
          onCompleted: (res) => {
            const id = _get(res, 'data.id');
            const backgroundJob = _get(res, 'data.backgroundJob') as
              BackgroundJob | undefined;
            toast({
              content: t('create-data-import-success'),
            });
            dataImportsQuery.refetch();
            if (backgroundJob) {
              jobEventBus.emit(EventBusEvents.JobCreated, backgroundJob);
            }
            if (id) setActiveImportId(id);
            if (id) setActiveImportIds((prev) => [...prev, id]);
          },
          onError: (error) => {
            setImportFormLoading(false);
            showGraphQLErrors(error, t);
          },
        });
      }
    },
    [
      activeImportId,
      dataImportCreateMutation,
      dataImportUpdateMutation,
      dataImportsQuery,
      t,
    ],
  );

  const onCancelDataImport = (item?: DataImport) => {
    if (item?.backgroundJob?.id) {
      cancelJobMutation({
        variables: { input: { id: item.backgroundJob.id } },
        onCompleted: () => {
          toast({
            content: t('cancel-data-import-success'),
          });
          if (activeImportId === item.id) {
            setActiveImportId(undefined);
          }
        },
        onError: (error) => {
          showGraphQLErrors(error, t);
        },
      });
    }
  };

  const onDestroyDataImport = (item?: DataImport) => {
    if (item?.id) {
      useBulkSelectResult.handleSelectSingleTableItem(item);
      openModal(Modals.DataImportDestroyModal);
    }
  };

  const onCloseDestroyDataImportModal = () => {
    closeModal(Modals.DataImportDestroyModal);
    useBulkSelectResult.handleUnselectAllTableItems();
    dataImportCreateMutationResult.reset();
  };

  const onConfirmedDestroyDataImport = () => {
    const id = useBulkSelectResult.selectedSingleTableItem?.id;
    if (id) {
      dataImportDestroyMutation({
        variables: { id },
        onCompleted: () => {
          closeModal(Modals.DataImportDestroyModal);
          toast({ content: t('destroy-data-import-success') });
          dataImportsQuery.refetch();
          useBulkSelectResult.handleUnselectAllTableItems();
        },
      });
    }
  };

  const onShowDataImportErrors = useCallback(
    (item?: DataImport) => {
      if (item?.id) {
        useBulkSelectResult.handleSelectSingleTableItem(item);
        openModal(Modals.DataImportErrorsModal);
      }
    },
    [openModal, useBulkSelectResult],
  );

  const onCloseDataImportErrorsModal = () => {
    closeModal(Modals.DataImportErrorsModal);
    useBulkSelectResult.handleUnselectAllTableItems();
  };

  const onResetForm = useCallback(() => {
    const values = formRef.current?.values;
    const resetValues = {
      ...IMPORT_FILTER_EMPTY_VALUES,
      ..._pick(values, [FieldNames.Type]),
    };
    formRef.current?.reset(resetValues);
    setActiveImportId(undefined);
  }, [formRef]);

  const isCreateDataImportAvailable =
    !dataImportsQuery.loading && dataImportsQuery.collection?.length === 0;

  useEffect(() => {
    if (activeImportIds.length === 0) {
      setImportFormLoading(false);
      return;
    }
    // Re-run with the current ids every time the tracked set changes: polling
    // otherwise keeps the variables of the first execution.
    fetchActiveImports({
      variables: {
        filters: [
          {
            id: {
              grouping: FilterGroupingEnum.And,
              predicate: {
                in: activeImportIds,
              },
              filterGroup: 'id',
            },
          },
        ],
      },
    });
    // `stopPolling` zeroes the observable's poll interval, so a re-execution
    // after a finished batch has to restart polling explicitly.
    activeImportsStartPolling(POLL_INTERVAL);
  }, [activeImportIds, fetchActiveImports, activeImportsStartPolling]);

  useEffect(() => {
    const canUpdate = _get(activeImport, ['canUpdate', 'value'], false);
    const status = _get(activeImport, 'status');
    if (!canUpdate && status === DataImportStatusEnum.Complete) {
      onResetForm();
      toast({
        content: t('complete-data-import-success'),
      });
    }
  }, [activeImport, activeImportId, onResetForm, t]);

  return {
    activeImportId,
    cancelJobMutationResult,
    config,
    dataImportCreateMutationResult,
    dataImportDestroyLoading: dataImportDestroyMutationResult.loading,
    dataImportDestroyMutationResult,
    dataImportQuery,
    dataImportUpdateMutationResult,
    dataImportsQuery,
    importFormLoading,
    isCreateDataImportAvailable,
    onCancelDataImport,
    onCloseDataImportErrorsModal,
    onCloseDestroyDataImportModal,
    onConfirmedDestroyDataImport,
    onDestroyDataImport,
    onResetForm,
    onShowDataImportErrors,
    onSubmitDataImportForm,
  };
}
